import React from "react";
import { useTranslation } from "react-i18next";
import { PiCalendarBlank, PiClock, PiMapPin, PiMapPinSimpleArea } from "react-icons/pi";
import dayjs from "dayjs";

import { Bill } from "../../models";
import Card from "./Card";
import PlateNumberPanel from "./PlateNumberPanel";
import { durationFormatter } from "../../formatters";

interface PaymentPanelProps {
  bills: Bill[];
}

const PaymentPanel: React.FC<PaymentPanelProps> = ({ bills }) => {
  const { t } = useTranslation();

  const [parkingDurations, setParkingDurations] = React.useState<number[]>([]);

  const totalAmount = bills.reduce((sum, bill) => sum + (bill.amt ?? 0), 0);

  React.useEffect(() => {
    if (!bills.length) return;
    const updateDurations = () => {
      setParkingDurations(
        bills.map((bill) =>
          bill.outDatetime
            ? bill.outDatetime.diff(bill.inDatetime ?? dayjs())
            : dayjs().diff(bill.inDatetime ?? dayjs()),
        ),
      );
    };
    updateDurations();
    const interval = setInterval(updateDurations, 1000);
    return () => clearInterval(interval);
  }, [bills]);

  const buildFooter = () => {
    if (bills.length === 1) {
      const bill = bills[0];
      return (
        <div className="space-y-4 text-xl font-light">
          <div className="flex gap-3">
            <div className="p-1.5 bg-primary/25 text-primary rounded-lg">
              <PiCalendarBlank />
            </div>
            <div className="flex-1 flex-between gap-3">
              <div>{t("bill.inTime")}:</div>
              <div>{bill.inDatetime?.format("YYYY.MM.DD - HH:mm")}</div>
            </div>
          </div>
          {!bill.active && (
            <div className="flex gap-3">
              <div className="p-1.5 bg-primary/25 text-primary rounded-lg">
                <PiCalendarBlank />
              </div>
              <div className="flex-1 flex-between gap-3">
                <div>{t("bill.outTime")}:</div>
                <div>{bill.outDatetime?.format("YYYY.MM.DD - HH:mm")}</div>
              </div>
            </div>
          )}
          <div className="flex gap-3">
            <div className="p-1.5 bg-primary/25 text-primary rounded-lg">
              <PiClock />
            </div>
            <div className="flex-1 flex-between gap-3">
              <div>{t("payment.parkingDuration")}:</div>
              <div className="text-primary">{durationFormatter(parkingDurations[0] ?? 0)}</div>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="p-1.5 bg-primary/25 text-primary rounded-lg">
              <PiMapPin />
            </div>
            <div className="flex-1 flex-between gap-3">
              <div>{t("payment.parkingName")}:</div>
              <div>{bill.parkingName}</div>
            </div>
          </div>
        </div>
      );
    } else if (bills.length > 1) {
      return (
        <div className="text-lg leading-5 space-y-4 border-t pt-4">
          {bills.map((bill, idx) => (
            <div key={bill.invoiceId}>
              <div className="flex-center gap-3">
                <PiMapPinSimpleArea />
                <div className="flex-1 flex-between gap-3">
                  <div>{bill.parkingName}</div>
                  <div className="text-xl">{bill.amt.toLocaleString("mn")}₮</div>
                </div>
              </div>
              <div className="flex-between gap-3 text-muted-foreground">
                <div>{bill.inDatetime.format("YYYY.MM.DD - HH:mm")}</div>
                <div>{durationFormatter(parkingDurations[idx])}</div>
              </div>
            </div>
          ))}
        </div>
      );
    }
  };

  return (
    <Card
      padding={"2rem"}
      gap={"2rem"}
      header={
        <div className="flex-center">
          <PlateNumberPanel plateNumber={bills[0].carStateNum} />
        </div>
      }
      footer={buildFooter()}
    >
      <div className="flex-center flex-col">
        <div className="text-lg font-light">{t("bill.amountToPay")}</div>
        <div className="text-4xl font-semibold">{totalAmount.toLocaleString("mn")}₮</div>
      </div>
    </Card>
  );
};

export default PaymentPanel;
